import { format } from 'date-fns';
import React, { useEffect, useState } from 'react';
import { getCashflow } from '../../api/sportaloka-client';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import 'jspdf-autotable';

const Cashflow = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [data, setData] = useState([]);
  const [paginationOptions, setPaginationOptions] = useState({
    start_date: format(new Date(), 'yyyy-MM-dd'),
    end_date: format(new Date(), 'yyyy-MM-dd')
  });

  const downloadExcel = () => {
    const worksheetData = data.map((item) => ({
      Tanggal: format(new Date(item.date), 'yyyy/MM/dd HH:mm:ss'),
      // Jenis: item.type,
      Keterangan: item.remarks,
      Masuk: item.type === 'INCOME' ? `IDR ${item.amount.toLocaleString()}` : '',
      Keluar: item.type === 'OUTCOME' ? `IDR ${item.amount.toLocaleString()}` : ''
    }));

    const totalIncome = data.reduce(
      (acc, curr) => (curr.type === 'INCOME' ? acc + curr.amount : acc),
      0
    );
    const totalOutcome = data.reduce(
      (acc, curr) => (curr.type === 'OUTCOME' ? acc + curr.amount : acc),
      0
    );
    const endingBalance = totalIncome - totalOutcome;

    const worksheet = XLSX.utils.json_to_sheet(worksheetData);
    XLSX.utils.sheet_add_aoa(
      worksheet,
      [
        [],
        ['Total Pemasukan', '', '', `IDR ${totalIncome.toLocaleString()}`],
        ['Total Beban', '', '', `IDR ${totalOutcome.toLocaleString()}`],
        ['Saldo Akhir', '', '', `IDR ${endingBalance.toLocaleString()}`]
      ],
      { origin: -1 }
    );

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'ArusKas');
    XLSX.writeFile(workbook, 'LaporanArusKasTenant.xlsx');
  };

  const downloadPDF = () => {
    const doc = new jsPDF();
    doc.text('Laporan Arus Kas', 10, 10);

    const tableColumn = ['Tanggal', 'Keterangan', 'Masuk', 'Keluar'];
    const tableRows = data.map((item) => [
      format(new Date(item.date), 'yyyy/MM/dd HH:mm:ss'),
      item.remarks,
      item.type === 'INCOME' ? `IDR ${item.amount.toLocaleString()}` : '',
      item.type === 'OUTCOME' ? `IDR ${item.amount.toLocaleString()}` : ''
    ]);

    const totalIncome = data.reduce(
      (acc, curr) => (curr.type === 'INCOME' ? acc + curr.amount : acc),
      0
    );
    const totalOutcome = data.reduce(
      (acc, curr) => (curr.type === 'OUTCOME' ? acc + curr.amount : acc),
      0
    );
    const endingBalance = totalIncome - totalOutcome;

    doc.autoTable(tableColumn, tableRows, { startY: 20 });
    doc.text(
      `Total Pemasukan: IDR ${totalIncome.toLocaleString()}`,
      14,
      doc.autoTable.previous.finalY + 10
    );
    doc.text(
      `Total Beban: IDR ${totalOutcome.toLocaleString()}`,
      14,
      doc.autoTable.previous.finalY + 20
    );
    doc.text(
      `Saldo Akhir: IDR ${endingBalance.toLocaleString()}`,
      14,
      doc.autoTable.previous.finalY + 30
    );

    doc.save('LaporanArusKasTenant.pdf');
  };

  const transformData = (income, outcome) => {
    const incomeData = income.map((item) => ({
      date: item.created_at,
      remarks: item.remarks,
      amount: item.amount,
      type: 'INCOME'
    }));

    const outcomeData = outcome.flatMap((item) => {
      return item.guest_costs.map((guestCost) => ({
        date: item.executed_at,
        remarks: `Biaya ${guestCost.tenant_cost.description} untuk tamu ${item.fullname} dengan kode booking ${item.booking.booking_code}`,
        amount: guestCost.tenant_cost.amount,
        type: 'OUTCOME'
      }));
    });
    return [...incomeData, ...outcomeData];
  };

  const getData = async () => {
    try {
      setLoading(true);
      setError(false);
      setData([]);
      const query = new URLSearchParams({
        start_date: paginationOptions.start_date,
        end_date: paginationOptions.end_date
      }).toString();
      const response = await getCashflow(query);
      const responseData = transformData(response.data.data.income, response.data.data.outcome);
      setData(responseData);
      setLoading(false);
    } catch (error) {
      setLoading(false);
      setError(true);
    }
  };

  useEffect(() => {
    getData();
  }, [paginationOptions]); // Use paginationOptions as dependency
  return (
    <div style={{ minHeight: 640 }}>
      <div>
        <div className="d-flex justify-content-between align-items-end">
          <h1 className="text-30 lh-14 fw-600 mb-0">Cashflow</h1>
          <div className="d-flex align-items-end">
            <div className="me-4">
              <label className="fw-semibold text-14">Start Date</label>
              <input
                onKeyDown={(e) => e.preventDefault()}
                onChange={(e) =>
                  setPaginationOptions({ ...paginationOptions, start_date: e.target.value })
                }
                value={paginationOptions.start_date}
                type="date"
                className="form-control"
                style={{ border: 'solid 1px #cccccc', backgroundColor: '#ffffff', width: 200 }}
              />
            </div>
            <div>
              <label className="fw-semibold text-14">End Date</label>
              <input
                onKeyUp={(e) => e.preventDefault()}
                onChange={(e) =>
                  setPaginationOptions({ ...paginationOptions, end_date: e.target.value })
                }
                value={paginationOptions.end_date}
                type="date"
                className="form-control"
                style={{ border: 'solid 1px #cccccc', backgroundColor: '#ffffff', width: 200 }}
              />
            </div>
            <div className="ms-4">
              <button onClick={getData} className="btn btn-small text-white fw-semibold bg-blue-1">
                Filter Data
              </button>
            </div>
            <div className="ms-4">
              <button
                onClick={downloadExcel}
                className="btn btn-small text-white fw-semibold bg-success">
                Download Excel
              </button>
            </div>
            <div className="ms-4">
              <button
                onClick={downloadPDF}
                className="btn btn-small text-white fw-semibold bg-danger">
                Download PDF
              </button>
            </div>
          </div>
        </div>
      </div>
      {loading && !error && (
        <div className="d-flex justify-content-center mt-60">
          <p className="fw-semibold text-blue-1">Loading...</p>
        </div>
      )}
      {!loading && error && (
        <div className="d-flex justify-content-center mt-60">
          <p className="fw-semibold text-dark">
            Gagal mendapatkan data,{' '}
            <span onClick={getData} className="cursor-pointer text-danger">
              Coba kembali
            </span>
          </p>
        </div>
      )}
      {!loading && !error && data.length === 0 && (
        <div className="d-flex justify-content-center mt-60">
          <p className="fw-semibold text-blue-1">Data tidak ditemukan</p>
        </div>
      )}
      {!loading && !error && data.length > 0 && (
        <table className="table table-border table-report mt-40">
          <thead>
            <tr>
              <th className="bg-blue-1 text-white rounded-0">Tanggal</th>
              {/* <th className="bg-blue-1 text-white rounded-0">Jenis</th> */}
              <th className="bg-blue-1 text-white rounded-0">Keterangan</th>
              <th className="text-right bg-blue-1 text-white rounded-0">Masuk</th>
              <th className="text-right bg-blue-1 text-white rounded-0">Keluar</th>
            </tr>
          </thead>
          <tbody>
            {data.map((item) => {
              return (
                <tr key={item.id}>
                  <td>{format(item.date, 'yyyy/MM/dd HH:mm:ss')}</td>
                  {/* <td>{item.type === 'INCOME' ? 'PEMASUKAN' : 'PENGELUARAN'}</td> */}
                  <td>{item.remarks}</td>
                  <td className="text-right">
                    {item.type === 'INCOME' && `IDR ${item.amount.toLocaleString()}`}
                  </td>
                  <td className="text-right">
                    {item.type === 'OUTCOME' && `IDR ${item.amount.toLocaleString()}`}
                  </td>
                </tr>
              );
            })}
          </tbody>
          <tfoot>
            <tr>
              <th colSpan={3}>Total Pemasukan</th>
              <th className="text-right">
                IDR{' '}
                {data
                  .reduce((acc, curr) => {
                    if (curr.type === 'INCOME') {
                      return acc + curr.amount;
                    }
                    return acc;
                  }, 0)
                  .toLocaleString()}
              </th>
            </tr>
            <tr>
              <th colSpan={3}>Total Beban</th>
              <th className="text-right">
                IDR{' '}
                {data
                  .reduce((acc, curr) => {
                    if (curr.type === 'OUTCOME') {
                      return acc + curr.amount;
                    }
                    return acc;
                  }, 0)
                  .toLocaleString()}
              </th>
            </tr>
            <tr>
              <th className="bg-blue-2 text-18 fw-bold" colSpan={3}>
                Saldo Akhir
              </th>
              <th className="text-right bg-blue-2 text-18 fw-bold">
                IDR{' '}
                {(
                  data.reduce((acc, curr) => {
                    if (curr.type === 'INCOME') {
                      return acc + curr.amount;
                    }
                    return acc;
                  }, 0) -
                  data.reduce((acc, curr) => {
                    if (curr.type === 'OUTCOME') {
                      return acc + curr.amount;
                    }
                    return acc;
                  }, 0)
                ).toLocaleString()}
              </th>
            </tr>
          </tfoot>
        </table>
      )}
    </div>
  );
};

export default Cashflow;
